/* ============================================================================
   Lab toolbar — review controls injected into every experiment page.
   ----------------------------------------------------------------------------
     Speed      Scales GSAP's global timeline. Drag to 0.15x to watch an easing
                curve resolve frame by frame. This is the thing Figma can't do
                and the reason the motion language gets settled here.
                (Scroll-scrubbed tweens are driven by scroll position, not
                time, so they ignore speed — scroll slowly instead.)
     Replay     Re-fires the page's entry animation without a reload.
     Reduced    Simulates prefers-reduced-motion so the fallback gets reviewed
                as a real design state, not discovered at QA.
     Grid       Orchard 12-col (6 on mobile) overlay to check motion against
                the layout system.
     Tuned      Appears only when tokens have been overridden in the Vocabulary
                playground. Those edits are live on every page, so the badge
                exists to stop anyone approving a timing that isn't actually in
                motion.css. Click it to revert.
     H          Hide/show the bar for clean screen recordings.
   ========================================================================= */

import { clearOverrides, readOverrides } from './motion.js';

const PREFS_KEY = 'figs-motion-lab-prefs';

function loadPrefs() {
  try {
    return JSON.parse(localStorage.getItem(PREFS_KEY)) || {};
  } catch {
    return {};
  }
}

function savePrefs(prefs) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* private browsing — preferences just won't persist */
  }
}

export function mountLabBar({ title = 'Experiment' } = {}) {
  const prefs = loadPrefs();

  // Single-pattern demos live under /experiments/NN-*/ and return to the
  // Motion demos library; the index pages return to the lab homepage.
  const isDemo = /^\/experiments\/[^/]+\//.test(location.pathname);
  const back = isDemo
    ? { href: '/experiments/', label: 'Demos', hint: 'Back to Motion demos' }
    : { href: '/', label: 'Lab', hint: 'Back to the lab index' };

  const bar = document.createElement('div');
  bar.className = 'lab-bar';
  bar.innerHTML = `
    <a class="lab-bar__home" href="${back.href}" title="${back.hint}">&#8592; ${back.label}</a>
    <span class="lab-bar__divider"></span>
    <span class="lab-bar__group" style="opacity:.75">${title}</span>
    <span class="lab-bar__divider"></span>
    <span class="lab-bar__group">
      <label for="lab-speed">Speed</label>
      <input id="lab-speed" class="lab-bar__speed" type="range"
             min="0.1" max="2" step="0.05" value="${prefs.speed ?? 1}">
      <span class="lab-bar__readout" data-readout>${Number(prefs.speed ?? 1).toFixed(2)}x</span>
    </span>
    <span class="lab-bar__divider"></span>
    <span class="lab-bar__group">
      <button class="lab-bar__btn" data-replay>Replay</button>
      <button class="lab-bar__btn" data-reduced aria-pressed="false">Reduced</button>
      <button class="lab-bar__btn" data-grid aria-pressed="false">Grid</button>
    </span>
    <span class="lab-bar__divider" data-tuned-divider hidden></span>
    <span class="lab-bar__group" data-tuned hidden>
      <button class="lab-bar__btn lab-bar__btn--tuned" data-tuned-btn>
        Tuned <b data-tuned-count></b>
      </button>
    </span>
  `;

  const handle = document.createElement('button');
  handle.className = 'lab-bar-handle';
  handle.type = 'button';
  handle.setAttribute('aria-label', 'Show lab controls');
  handle.textContent = '▲';
  handle.hidden = true;

  const overlay = document.createElement('div');
  overlay.className = 'lab-grid';
  const columns = Number(
    getComputedStyle(document.documentElement).getPropertyValue('--grid-columns')
  ) || 12;
  overlay.innerHTML = `<div class="lab-grid__inner">${'<div class="lab-grid__col"></div>'.repeat(
    columns
  )}</div>`;

  document.body.append(overlay, bar, handle);

  /* --- Speed ------------------------------------------------------------ */
  const speed = bar.querySelector('#lab-speed');
  const readout = bar.querySelector('[data-readout]');

  const applySpeed = (value) => {
    readout.textContent = `${Number(value).toFixed(2)}x`;
    window.gsap?.globalTimeline.timeScale(Number(value));
    savePrefs({ ...loadPrefs(), speed: Number(value) });
  };
  applySpeed(speed.value);
  speed.addEventListener('input', (e) => applySpeed(e.target.value));

  /* --- Replay -----------------------------------------------------------
     Experiments listen for this and rebuild their entry timeline. Scroll is
     returned to the top first so scroll-triggered work re-fires naturally. */
  bar.querySelector('[data-replay]').addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    window.ScrollTrigger?.refresh();
    window.dispatchEvent(new CustomEvent('mo:replay'));
  });

  /* --- Reduced-motion simulation ---------------------------------------- */
  const reducedBtn = bar.querySelector('[data-reduced]');
  const setReduced = (on) => {
    reducedBtn.setAttribute('aria-pressed', String(on));
    document.documentElement.dataset.moReduced = String(on);
    window.ScrollTrigger?.refresh();
    window.dispatchEvent(new CustomEvent('mo:replay'));
  };
  reducedBtn.addEventListener('click', () =>
    setReduced(reducedBtn.getAttribute('aria-pressed') !== 'true')
  );

  /* --- Tuned-token badge -------------------------------------------------
     Tokens edited in the Vocabulary playground apply to every page, which is
     what makes the playground useful — you retune `--mo-slow` on the index and
     come here to see what it did. It's also how someone signs off on a timing
     that isn't in motion.css. So the badge is always visible while overrides
     are live, and clicking it drops them. */
  const tuned = bar.querySelector('[data-tuned]');
  const tunedDivider = bar.querySelector('[data-tuned-divider]');
  const tunedCount = bar.querySelector('[data-tuned-count]');

  const syncTuned = () => {
    const n = Object.keys(readOverrides()).length;
    tuned.hidden = !n;
    tunedDivider.hidden = !n;
    tunedCount.textContent = n ? String(n) : '';
    tuned.title = n
      ? `${n} token${n === 1 ? '' : 's'} overridden from the Vocabulary playground. Click to revert to motion.css.`
      : '';
  };

  bar.querySelector('[data-tuned-btn]').addEventListener('click', () => {
    clearOverrides();
    syncTuned();
    window.ScrollTrigger?.refresh();
    window.dispatchEvent(new CustomEvent('mo:replay'));
  });

  syncTuned();
  window.addEventListener('mo:replay', syncTuned);

  /* --- Grid overlay ------------------------------------------------------ */
  const gridBtn = bar.querySelector('[data-grid]');
  gridBtn.addEventListener('click', () => {
    const on = gridBtn.getAttribute('aria-pressed') !== 'true';
    gridBtn.setAttribute('aria-pressed', String(on));
    overlay.dataset.on = String(on);
  });

  /* --- Hide for screen recording ---------------------------------------- */
  const setCollapsed = (on) => {
    bar.dataset.collapsed = String(on);
    handle.hidden = !on;
  };
  handle.addEventListener('click', () => setCollapsed(false));
  window.addEventListener('keydown', (e) => {
    if (e.key.toLowerCase() !== 'h' || e.metaKey || e.ctrlKey || e.altKey) return;
    if (/^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName || '')) return;
    setCollapsed(bar.dataset.collapsed !== 'true');
  });

  return { bar, setReduced, setCollapsed };
}
